/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { CheckOutSuccessResponse } from '../../../helpers/chargebee/types';
import type { BasicFunction } from '../../../helpers/jsHelpers/types';
import type { DmsSettingMap } from '../../../libs/netgemLibrary/dms/types/DeviceInfoSettings';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { AppConfigurationState } from '../../../redux/appConf/reducers';
import type { ModalState } from '../../../redux/modal/reducers';
import type { BO_API_PURCHASE_RESPONSE_TYPE, BO_API_PURCHASE_STATUS_RESPONSE_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/purchase';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { MESSAGE_MODAL_TYPE } from '../messageModal/MessageModal';

import { openCheckOut } from '../../../helpers/applicationCustomization/externalPaymentSystem';
import { SECONDS_PER_DAY, SECONDS_PER_HOUR, getIso8601DurationInSeconds } from '../../../helpers/dateTime/Format';
import { logError } from '../../../helpers/debug/debug';
import { type DEEPLINK_GENERATION_DATA, generateLegacyDeeplink } from '../../../helpers/debug/deeplink';
import SentryWrapper from '../../../helpers/debug/sentry';
import { SentryTagName, SentryTagValue } from '../../../helpers/debug/sentryTypes';
import { parseBoolean } from '../../../helpers/jsHelpers/parser';
import Localizer from '../../../helpers/localization';
import { getPriceAsFloat, getPriceAsInteger } from '../../../helpers/maths/maths';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { type VOD_PURCHASE_DATA_TYPE, clearPendingPurchase } from '../../../helpers/rights/pendingOperations';
import { getEpisodeIndexAndTitle, getTitle, renderProgramLanguage, renderProgramParentalGuidance } from '../../../helpers/ui/metadata/Format';
import { Currency, getCurrencySymbol } from '../../../helpers/ui/metadata/Types';
import { renderPurchaseSummary } from '../../../helpers/videofutur/metadata';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { CustomNetworkError } from '../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { HttpStatus } from '../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { getItemContentType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import {
  BO_PURCHASE_ALREADY_PURCHASED,
  BO_PURCHASE_DEVICE_NOT_FOUND,
  BO_PURCHASE_INVALID_CONTENT,
  BO_PURCHASE_INVALID_PROMOCODE,
  BO_PURCHASE_INVALID_PROMOCODE_FOR_TITLE,
  BO_PURCHASE_INVALID_USER_IP,
  BO_PURCHASE_STATUS_OK,
} from '../../../libs/netgemLibrary/videofutur/types/ErrorCodes';
import { disableModals, enableModals, hideModal, showMessageModal } from '../../../redux/modal/actions';
import { buildErrorResponse } from '../../../redux/netgemApi/actions/helpers/bo';
import { getSettingValueByName } from '../../../redux/netgemApi/actions/helpers/settings';
import sendV8MetadataLocationRequest from '../../../redux/netgemApi/actions/v8/metadataLocation';
import { purchase } from '../../../redux/netgemApi/actions/videofutur/helpers/purchase';
import { stopOpenStreams } from '../../../redux/netgemApi/actions/videofutur/helpers/stopOpenStreams';
import sendBOPurchaseStatusRequest from '../../../redux/netgemApi/actions/videofutur/purchaseStatus';
import { type BO_API_PURCHASE_STATUS_SUCCESS_TYPE, Purchase, type PurchaseType } from '../../../redux/netgemApi/actions/videofutur/types/purchase';
import ButtonFX from '../../buttons/ButtonFX';
import { WidthKind } from '../../buttons/types';
import { PictoCheck, PictoInfo } from '../../pictos/Element';
import DrmDiagnosis from '../../player/diagnosis/diagnosis';
import Tooltip from '../../tooltip/Tooltip';
import Modal, { ModalView } from '../modal';
import PurchaseRedirect from './PurchaseRedirect';
import './VodPurchaseModal.css';

// If duration is greater than 48h, display it as days (in seconds)
const MAX_HOURS_DISPLAYED = 172_800;

const DEFAULT_RENT_VALIDITY = '48h';

// Consecutive steps in the purchase journey
const STEP_1: number = 1;
const STEP_2: number = 2;

export type CloseCallbackType = {
  locationId?: string,
  playNow: boolean,
  viewingHistoryId?: string,
  vtiId?: number,
};

type ReduxVodPurchaseModalReducerStateType = {
  +appConfiguration: AppConfigurationState,
  +deviceOS: string,
  +deviceSettings: DmsSettingMap,
  +isDebugModeEnabled: boolean,
  +isExternalCheckOutEnabled: boolean,
};

type ReduxVodPurchaseModalDispatchToPropsType = {
  +localDisableModals: BasicFunction,
  +localEnableModals: BasicFunction,
  +localGetPurchaseStatus: (distributorId: string, vtiId: number, promocode: string, signal?: AbortSignal) => Promise<BO_API_PURCHASE_STATUS_RESPONSE_TYPE>,
  +localHideModal: BasicFunction,
  +localPurchase: (distributorId: string, vtiId: number, promocode: string | null, signal?: AbortSignal) => Promise<BO_API_PURCHASE_RESPONSE_TYPE>,
  +localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel: string) => void,
  +localStopOpenStreams: (distributorId: string, signal?: AbortSignal) => Promise<number>,
};

type CompleteVodPurchaseModalPropType = {
  ...ReduxVodPurchaseModalReducerStateType,
  ...ReduxVodPurchaseModalDispatchToPropsType,
  ...ModalState,
};

type VodPurchaseModalStateType = {
  appliedPromocode: string | null,
  discountPrice: string | null,
  errorMessageKey: string | null,
  isApplyingPromocode: boolean,
  isPaying: boolean,
  isPromocodeFocusHidden: boolean,
  promocode: string,
  promocodeError: string | null,
  rentValidity: string,
  showRedirect: boolean,
  step: number,
  title: { line1: string | null, line2: string | null } | null,
  updatedPrice: string | null,
  vodLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
};

const InitialState = Object.freeze({
  appliedPromocode: null,
  discountPrice: null,
  errorMessageKey: null,
  isApplyingPromocode: false,
  isPaying: false,
  isPromocodeFocusHidden: false,
  promocode: '',
  promocodeError: null,
  rentValidity: '48h',
  showRedirect: false,
  step: STEP_1,
  title: null,
  updatedPrice: null,
  vodLocationMetadata: null,
});

class VodPurchaseModalView extends React.PureComponent<CompleteVodPurchaseModalPropType, VodPurchaseModalStateType> {
  abortController: AbortController;

  closingEnableTimer: TimeoutID | null;

  inputRef: HTMLElement | null;

  modal: ModalView | null;

  constructor(props: CompleteVodPurchaseModalPropType) {
    super(props);

    this.abortController = new AbortController();
    this.closingEnableTimer = null;
    this.inputRef = null;

    this.state = {
      ...InitialState,
      showRedirect: parseBoolean(getSettingValueByName('vod', 'pay_on_mobile', props.appConfiguration)),
    };
  }

  componentDidMount() {
    this.initialize();

    // Clear any pending guest purchase since it's being displayed right now
    clearPendingPurchase();
  }

  componentDidUpdate(prevProps: CompleteVodPurchaseModalPropType) {
    const { vodPurchaseData } = this.props;
    const { vodPurchaseData: prevVodPurchaseData } = prevProps;

    if (vodPurchaseData !== prevVodPurchaseData) {
      this.initialize();
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component VodPurchaseModal will unmount');

    if (this.closingEnableTimer) {
      clearTimeout(this.closingEnableTimer);
    }
  }

  initialize = () => {
    const { vodPurchaseData } = this.props;

    if (!vodPurchaseData) {
      return;
    }

    this.buildPurchaseTitle(vodPurchaseData);

    const { locationId, vodLocationMetadata } = vodPurchaseData;

    if (!vodLocationMetadata && locationId) {
      // Query VOD location metadata to get viewingHistoryId and available audio/subtitles (first episode is used for pack purchase)
      this.loadVodLocationMetadata(locationId);
    }

    // Check VIP account (and bad configuration)
    this.applyPromocode('');
  };

  buildPurchaseTitle = (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => {
    const { itemCount, programMetadata, purchaseType, seriesMetadata } = vodPurchaseData;

    const seriesTitles = getTitle(seriesMetadata, Localizer.language);

    let programTitle = null;
    let episodeCount = null;

    if (purchaseType === Purchase.BuyPack) {
      episodeCount = Localizer.localize('modal.vod_purchase.pack_item_count', { count: itemCount });
    } else if (programMetadata) {
      const { episodeIndex, episodeTitle } = getEpisodeIndexAndTitle(programMetadata, seriesTitles, Localizer.language);
      const fixedEpisodeTitle = episodeTitle ?? Localizer.localize('modal.vod_purchase.episode_index', { index: episodeIndex ?? '' });
      programTitle = seriesTitles ? fixedEpisodeTitle : getTitle(programMetadata, Localizer.language);
    }

    this.setState({
      title: {
        line1: seriesTitles ?? programTitle,
        line2: seriesTitles ? (episodeCount ?? programTitle) : null,
      },
    });
  };

  getFullTitle = (): string => {
    const { title } = this.state;

    if (!title) {
      return Localizer.localize('vod.untitled');
    }

    const { line1, line2 } = title;

    if (line1 === null && line2 === null) {
      return Localizer.localize('vod.untitled');
    }

    // NOTE: Flow thinks line1 could be null here, which is obviously wrong

    if (line2 === null) {
      // Full title: <Program title>
      return line1 ?? '';
    }

    // Full title: <Series titles - episode title> or <Series title - episode count>
    return `${line1 ?? ''} - ${line2}`;
  };

  loadVodLocationMetadata = (locationId: string) => {
    const { localSendV8MetadataLocationRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8MetadataLocationRequest(locationId, signal)
      .then(({ result: vodLocationMetadata }) => {
        signal.throwIfAborted();

        const rentValidity = this.formatLicenceDuration(vodLocationMetadata?.location.purchaseInfo?.licenseDuration) ?? DEFAULT_RENT_VALIDITY;
        this.setState({ rentValidity, vodLocationMetadata });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  handlePlayNowButtonOnClick = () => {
    const { vodPurchaseData } = this.props;
    const { vodLocationMetadata: vodLocationMetadataState } = this.state;
    const { modal } = this;

    if (!vodPurchaseData) {
      logError('Cannot play because of missing purchase data');
      return;
    }

    const { locationId, viewingHistoryId, vodLocationMetadata, vtiId } = vodPurchaseData;
    const localVodLocation = vodLocationMetadata ?? vodLocationMetadataState?.location;

    // Play movie or first episode of season
    modal?.close({
      locationId,
      playNow: true,
      viewingHistoryId: localVodLocation?.providerInfo?.viewingHistoryId ?? viewingHistoryId,
      // Use vtiId from vodLocationMetadata because in case of pack purchase, the one from vodPurchaseData is the pack's one
      vtiId: localVodLocation?.purchaseInfo?.vtiId ?? vtiId,
    });
  };

  handlePlayLaterButtonOnClick = () => {
    const { modal } = this;

    modal?.close({ playNow: false });
  };

  handlePromocodeOnBlur = () => {
    this.setState({ isPromocodeFocusHidden: false });
  };

  handlePromocodeOnMouseDown = () => {
    this.setState({ isPromocodeFocusHidden: true });
  };

  handlePromocodeOnKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>) => {
    const { key } = event;

    if (key === 'Enter') {
      this.handleApplyPromocodeButtonOnClick();
    }
  };

  handlePromocodeOnChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const {
      currentTarget: { validity, value },
    } = event;

    if (validity.valid || value === '') {
      this.setState({ promocode: value });
    }
  };

  handlePromocodeOK = (response: BO_API_PURCHASE_STATUS_SUCCESS_TYPE, vtiId: number, displayPrice: string, appliedPromocode: string) => {
    const { price: discountPrice } = response;

    const originalPrice = getPriceAsFloat(displayPrice);

    if (typeof discountPrice === 'number' && typeof originalPrice === 'number' && discountPrice !== originalPrice) {
      if (discountPrice < originalPrice) {
        // Active discount or promocode applied
        this.setState({
          appliedPromocode,
          discountPrice: `${discountPrice.toString().replace('.', ',')} ${getCurrencySymbol(Currency.Euro)}`,
          promocode: '',
          promocodeError: null,
        });
      } else {
        /*
         * Current price is greater than original price (expired discount but cache issue)
         * See https://netgem.atlassian.net/browse/GM-251
         */
        const updatedPrice = `${discountPrice.toString().replace('.', ',')} ${getCurrencySymbol(Currency.Euro)}`;
        this.setState({ updatedPrice });

        SentryWrapper.warning({
          context: {
            appliedPromocode,
            discountPrice,
            originalPrice,
            updatedPrice,
            vtiId,
          },
          message: 'Real price higher than display price',
          tagName: SentryTagName.Component,
          tagValue: SentryTagValue.VodPurchase,
        });
      }
    }
  };

  getLocalizedErrorMessage = (status: string): string => {
    if (status === BO_PURCHASE_INVALID_CONTENT) {
      return 'modal.vod_purchase.invalid_content';
    }

    if (status === BO_PURCHASE_INVALID_USER_IP) {
      return 'modal.vod_purchase.geolocked_content';
    }

    // Generic error
    return 'modal.vod_purchase.promocode_error';
  };

  promocodeAppliedCallback = (appliedPromocode: string, vtiId: number, response: BO_API_PURCHASE_STATUS_RESPONSE_TYPE, displayPrice: string = '') => {
    const { modal } = this;
    const { status } = response;

    this.setState({ isApplyingPromocode: false });

    switch (status) {
      case BO_PURCHASE_STATUS_OK: {
        // Promocode applied
        if ('price' in response) {
          this.handlePromocodeOK(response, vtiId, displayPrice, appliedPromocode);
        }
        break;
      }

      case BO_PURCHASE_INVALID_PROMOCODE:
      case BO_PURCHASE_INVALID_PROMOCODE_FOR_TITLE: {
        // Invalid promocode
        this.setState({ promocodeError: Localizer.localize('modal.vod_purchase.invalid_promocode') });
        window.requestAnimationFrame(() => this.inputRef?.focus());
        break;
      }

      case BO_PURCHASE_ALREADY_PURCHASED: {
        // Item has already been purchased: refresh purchase list
        Messenger.emit(MessengerEvents.REFRESH_PURCHASE_LIST);
        modal?.close();
        break;
      }

      case BO_PURCHASE_DEVICE_NOT_FOUND: {
        // Token expired (device probably disconnected)
        Messenger.emit(MessengerEvents.AUTHENTICATION_REQUIRED);
        break;
      }

      case BO_PURCHASE_INVALID_CONTENT:
      case BO_PURCHASE_INVALID_USER_IP:
      default: {
        /*
         * Potential BO errors at this point:
         *  - BO_PURCHASE_INVALID_CONTENT: content should have never been visible
         *  - BO_PURCHASE_INVALID_USER_IP: content is geolocked
         *  - unexpected error
         *
         * In any case, purchase is prevented
         */
        this.setState({ errorMessageKey: this.getLocalizedErrorMessage(status) });
        break;
      }
    }
  };

  applyPromocode = (promocode: string) => {
    const { localGetPurchaseStatus, vodPurchaseData } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (!vodPurchaseData) {
      return;
    }

    const {
      displayPrice,
      distributorId,
      item: { id },
      vtiId,
    } = vodPurchaseData;

    if (!vtiId) {
      logError(`No VTI Id for ${id}`);
      return;
    }

    if (!distributorId) {
      logError(`No distributor Id for ${id}`);
      return;
    }

    this.setState({
      isApplyingPromocode: true,
      promocodeError: null,
    });

    localGetPurchaseStatus(distributorId, vtiId, promocode, signal)
      .then((response: BO_API_PURCHASE_STATUS_RESPONSE_TYPE) => {
        signal.throwIfAborted();

        this.promocodeAppliedCallback(promocode, vtiId, response, displayPrice);
      })
      .catch((error: CustomNetworkError) => ignoreIfAborted(signal, error, () => this.promocodeAppliedCallback(promocode, vtiId, buildErrorResponse(null, error), displayPrice)));
  };

  handleApplyPromocodeButtonOnClick = () => {
    const { vodPurchaseData } = this.props;
    const { isApplyingPromocode, promocode } = this.state;

    if (isApplyingPromocode || !promocode || !vodPurchaseData || !vodPurchaseData.vtiId) {
      return;
    }

    this.applyPromocode(promocode);
  };

  paidCallback = (response?: BO_API_PURCHASE_RESPONSE_TYPE | CheckOutSuccessResponse) => {
    const { modal } = this;
    const status = response?.status;

    if (status === BO_PURCHASE_STATUS_OK || response?.httpCode === HttpStatus.OK) {
      // Payment OK
      this.setState({
        isPaying: false,
        step: STEP_2,
      });
      return;
    }

    if (status === BO_PURCHASE_ALREADY_PURCHASED) {
      // Item has already been purchased: refresh purchase list
      Messenger.emit(MessengerEvents.REFRESH_PURCHASE_LIST);
      modal?.close();
      return;
    }

    if (status === BO_PURCHASE_DEVICE_NOT_FOUND) {
      // Token expired (device probably disconnected)
      Messenger.emit(MessengerEvents.AUTHENTICATION_REQUIRED);
      return;
    }

    // Error while paying
    Messenger.emit(MessengerEvents.NOTIFY_ERROR, response?.errorMsg || Localizer.localize('common.messages.errors.retry'));

    this.setState({ isPaying: false });
  };

  isNotFree = (price: string): boolean => {
    const n = getPriceAsInteger(price);
    return n !== null ? n > 0 : true;
  };

  handlePayButtonOnClick = () => {
    const { deviceSettings, isDebugModeEnabled, localDisableModals, localEnableModals, localHideModal, localShowMessage, localStopOpenStreams, vodPurchaseData } = this.props;
    const { isApplyingPromocode, isPaying } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (isApplyingPromocode || isPaying || !vodPurchaseData || !vodPurchaseData.vtiId) {
      return;
    }

    const {
      distributorId,
      item: { id },
    } = vodPurchaseData;

    if (!distributorId) {
      logError(`No distributor Id for ${id}`);
      return;
    }

    this.setState({ isPaying: true }, () => {
      localStopOpenStreams(distributorId, signal)
        .then(async () => {
          signal.throwIfAborted();

          // Temporarily hide all modals so test video is visible
          localDisableModals();

          let canPlay = false;
          try {
            canPlay = await new DrmDiagnosis(deviceSettings, isDebugModeEnabled).runDiagnosis();
          } finally {
            // Show back all modals after diagnosis finishes
            localEnableModals();
          }

          if (canPlay) {
            this.purchaseInternal(vodPurchaseData, distributorId);
          } else {
            // Show message telling user to upgrade/change their browser
            Messenger.once(MessengerEvents.MODAL_CLOSED, localHideModal);
            localShowMessage(
              {
                buttonText: Localizer.localize('common.actions.ok'),
                message: Localizer.localize('common.messages.errors.drm_not_supported.message'),
                title: Localizer.localize('common.messages.errors.drm_not_supported.title'),
              },
              Localizer.localize('accessibility.modal.message_transaction_canceled'),
            );
          }
        })
        .catch((error) => ignoreIfAborted(signal, error, () => this.paidCallback(buildErrorResponse(null, error))));
    });
  };

  purchaseInternal = (vodPurchaseData: VOD_PURCHASE_DATA_TYPE, distributorId: string) => {
    const { isExternalCheckOutEnabled, localDisableModals, localEnableModals, localPurchase } = this.props;
    const { appliedPromocode, discountPrice, updatedPrice } = this.state;

    const {
      abortController: { signal },
      modal,
    } = this;

    const { displayPrice, vtiId } = vodPurchaseData;

    if (isExternalCheckOutEnabled && this.isNotFree(discountPrice || updatedPrice || displayPrice)) {
      // External payment system (e.g. Chargebee)

      // Prevent from closing modal by clicking outside when the user only wants to close the tooltip
      modal?.disableClosing();

      const title = this.getFullTitle();

      // Temporarily hide all modals so that Chargebee's popup is visible
      localDisableModals();

      openCheckOut(vtiId, title, updatedPrice || displayPrice, appliedPromocode, discountPrice)
        .then((response?: CheckOutSuccessResponse) => {
          /*
           * Refresh purchase list to update purchased item
           * NOTE: It's even refreshed when user closed the popup because we're not sure (yet)
           * if the transaction could finish without the app being aware of it
           */
          Messenger.emit(MessengerEvents.REFRESH_PURCHASE_LIST);

          if (response === undefined) {
            // Checkout has been canceled (probably by closing the popup)
            SentryWrapper.info({
              context: {
                appliedPromocode,
                discountPrice,
                displayPrice,
                title,
                updatedPrice,
                vtiId,
              },
              message: 'User closed Chargebee popup',
              tagName: SentryTagName.Component,
              tagValue: SentryTagValue.VodPurchase,
            });

            modal?.close({ playNow: false });
            return;
          }

          this.paidCallback(response);
        })
        .catch((error: Error) => {
          this.paidCallback(buildErrorResponse(null, error));
        })
        .finally(() => {
          // Show back all modals now that Chargebee's popup is closed
          localEnableModals();
        });
    } else {
      // Direct BO payment
      localPurchase(distributorId, vtiId, appliedPromocode, signal)
        .then((response: BO_API_PURCHASE_RESPONSE_TYPE) => {
          signal.throwIfAborted();

          this.paidCallback(response);
        })
        .catch((error) => ignoreIfAborted(signal, error, () => this.paidCallback(buildErrorResponse(null, error))));
    }
  };

  renderTitles = (): React.Node => {
    const { title } = this.state;

    if (!title) {
      return null;
    }

    const { line1, line2 } = title;

    return (
      <div className='titles'>
        <div>{line1}</div>
        {line2 ? <div className='subtitle'>{line2}</div> : null}
      </div>
    );
  };

  formatLicenceDuration = (licenseDuration?: string): string | null => {
    if (!licenseDuration) {
      // Buy case
      return null;
    }

    const duration = getIso8601DurationInSeconds(licenseDuration);

    if (duration <= MAX_HOURS_DISPLAYED) {
      return Localizer.localize('modal.vod_purchase.licence_duration_hour', { count: duration / SECONDS_PER_HOUR });
    }

    return Localizer.localize('modal.vod_purchase.licence_duration_hour', { count: Math.round(duration / SECONDS_PER_DAY) });
  };

  renderInformationTooltipContent = (validity: string): React.Node => (
    <div className='tooltipContent vodInformation'>
      <div className='title'>{Localizer.localize('modal.vod_purchase.help.title')}</div>
      <div className='subtitle'>{Localizer.localize('modal.vod_purchase.help.rent_title')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.rent_validity', { validity })}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.rent_plays')}</div>
      <div className='subtitle'>{Localizer.localize('modal.vod_purchase.help.buy_title')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.buy_validity')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.buy_plays')}</div>
      <div className='subtitle'>{Localizer.localize('modal.vod_purchase.help.video_quality_title')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.video_quality_best')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.video_quality_4k_hd')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.video_quality_4k_compatibility')}</div>
      <div className='subtitle'>{Localizer.localize('modal.vod_purchase.help.promocode_title')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.promocode_type')}</div>
      <div className='bullet'>{Localizer.localize('modal.vod_purchase.help.promocode_format')}</div>
      <div className='customerService'>{Localizer.localize('modal.vod_purchase.help.customer_service')}</div>
    </div>
  );

  renderPurchaseValidity = (purchaseType: PurchaseType, validity: string): React.Node => {
    if (purchaseType === Purchase.Rent) {
      // Rent
      return (
        <>
          <div className='bullet'>{Localizer.localize('modal.vod_purchase.info.rent.validity', { validity })}</div>
          <div className='bullet'>{Localizer.localize('modal.vod_purchase.info.rent.concurrency')}</div>
        </>
      );
    }

    // Buy
    return (
      <>
        <div className='bullet'>{Localizer.localize('modal.vod_purchase.info.buy.validity')}</div>
        <div className='bullet'>{Localizer.localize('modal.vod_purchase.info.buy.concurrency')}</div>
      </>
    );
  };

  renderContent = (): React.Node => {
    const { vodPurchaseData } = this.props;
    const { appliedPromocode, discountPrice, errorMessageKey, isApplyingPromocode, isPaying, isPromocodeFocusHidden, promocode, promocodeError, rentValidity, updatedPrice, vodLocationMetadata } =
      this.state;

    if (errorMessageKey || !vodPurchaseData) {
      return (
        <div className='invalidContent' ref={(el) => el?.focus()} role='alert' tabIndex={-1}>
          {Localizer.localize(errorMessageKey ?? 'modal.vod_purchase.promocode_error')}
        </div>
      );
    }

    const { definition, displayPrice, item, purchaseType } = vodPurchaseData;

    return (
      <div className='validation'>
        <div className='summary'>{renderPurchaseSummary(purchaseType, definition, displayPrice, discountPrice, updatedPrice)}</div>
        <div className='details other'>
          {renderProgramLanguage(vodLocationMetadata?.location ?? null)}
          {renderProgramParentalGuidance(item)}
        </div>
        <div className='validity'>{this.renderPurchaseValidity(purchaseType, rentValidity)}</div>
        <div className='codeAndButton'>
          <label className='screenReaderOnly' htmlFor='promocode-input'>
            {Localizer.localize('accessibility.modal.enter_promocode')}
          </label>
          <input
            aria-describedby={promocodeError ? 'promocode-error' : undefined}
            aria-invalid={Boolean(promocodeError)}
            className={clsx(isPromocodeFocusHidden && 'hiddenFocus')}
            disabled={isApplyingPromocode || (appliedPromocode !== null && appliedPromocode !== '')}
            id='promocode-input'
            onBlur={this.handlePromocodeOnBlur}
            onChange={this.handlePromocodeOnChange}
            onKeyDown={this.handlePromocodeOnKeyDown}
            onMouseDown={this.handlePromocodeOnMouseDown}
            pattern='[a-zA-Z0-9]+'
            placeholder={Localizer.localize('modal.vod_purchase.promocode')}
            ref={(instance: HTMLElement | null) => {
              this.inputRef = instance;
            }}
            type='text'
            value={appliedPromocode ? appliedPromocode : promocode}
          />
          {appliedPromocode ? (
            <PictoCheck />
          ) : (
            <ButtonFX hasPadding isDisabled={promocode === ''} isLoading={isApplyingPromocode} onClick={this.handleApplyPromocodeButtonOnClick} widthKind={WidthKind.Small}>
              {Localizer.localize('modal.vod_purchase.apply_promocode')}
            </ButtonFX>
          )}
        </div>
        <div aria-live='polite' className={clsx('promocodeError', promocodeError !== null && 'visible')} id='promocode-error'>
          {promocodeError}
        </div>
        <ButtonFX isDisabled={isApplyingPromocode} isLoading={isPaying} onClick={this.handlePayButtonOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('modal.vod_purchase.pay')}
        </ButtonFX>
        <Tooltip content={this.renderInformationTooltipContent(rentValidity)} placement='top'>
          <button className='information' type='button'>
            <PictoInfo />
            <div>{Localizer.localize('modal.vod_purchase.information')}</div>
          </button>
        </Tooltip>
      </div>
    );
  };

  handleClose = () => {
    const { modal } = this;

    modal?.close();
  };

  renderChildren = (): React.Node => {
    const { deviceOS, vodPurchaseData } = this.props;
    const { showRedirect, step } = this.state;

    if (showRedirect && vodPurchaseData) {
      // Purchase is not allowed on this device: display a QR code to redirect to mobile apps

      const {
        item,
        programMetadata,
        programMetadata: { authority },
        seriesMetadata,
      } = vodPurchaseData;

      const data: DEEPLINK_GENERATION_DATA = {
        authority,
        contentType: getItemContentType(item, authority),
        deviceOS,
        item,
        programMetadata,
        seriesMetadata: seriesMetadata ?? null,
        vodLocationsMetadata: null,
      };

      return <PurchaseRedirect onClose={this.handleClose} url={generateLegacyDeeplink(data)} />;
    }

    if (step === STEP_2) {
      // Step 2: Purchase finished

      return (
        <div className='paid'>
          <div className='title'>{Localizer.localize('modal.vod_purchase.paid.title')}</div>
          <div className='message'>{Localizer.localize('modal.vod_purchase.paid.message')}</div>
          <div className='buttons'>
            <ButtonFX onClick={this.handlePlayNowButtonOnClick}>{Localizer.localize('modal.vod_purchase.paid.watch_now')}</ButtonFX>
            <ButtonFX onClick={this.handlePlayLaterButtonOnClick}>{Localizer.localize('modal.vod_purchase.paid.watch_later')}</ButtonFX>
          </div>
        </div>
      );
    }

    // Step 1: User optionally enters a code and pays

    if (!vodPurchaseData) {
      return null;
    }

    return (
      <>
        {this.renderTitles()}
        {this.renderContent()}
      </>
    );
  };

  setModalRef = (instance: ModalView | null) => {
    this.modal = instance;
  };

  render(): React.Node {
    const { vodPurchaseData } = this.props;

    if (!vodPurchaseData) {
      return null;
    }

    return (
      <Modal ariaLabel={Localizer.localize('accessibility.modal.vod_purchase')} className='vodPurchase' innerRef={this.setModalRef}>
        {this.renderChildren()}
      </Modal>
    );
  }
}

const mapStateToProps = (state: CombinedReducers) => {
  return {
    appConfiguration: state.appConfiguration,
    deviceOS: state.appConfiguration.deviceOS,
    deviceSettings: state.appConfiguration.deviceSettings,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isExternalCheckOutEnabled: state.appConfiguration.isExternalCheckOutEnabled,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxVodPurchaseModalDispatchToPropsType => {
  return {
    localDisableModals: (): void => dispatch(disableModals()),

    localEnableModals: (): void => dispatch(enableModals()),

    localGetPurchaseStatus: (distributorId: string, vtiId: number, promocode: string, signal?: AbortSignal): Promise<BO_API_PURCHASE_STATUS_RESPONSE_TYPE> =>
      dispatch(sendBOPurchaseStatusRequest(distributorId, vtiId, promocode, signal)),

    localHideModal: (): void => dispatch(hideModal()),

    localPurchase: (distributorId: string, vtiId: number, promocode: string | null, signal?: AbortSignal): Promise<BO_API_PURCHASE_RESPONSE_TYPE> =>
      dispatch(purchase(distributorId, vtiId, promocode ?? '', signal)),

    localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8MetadataLocationRequest(assetId, signal)),

    localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel: string): void => dispatch(showMessageModal(messageData, ariaLabel)),

    localStopOpenStreams: (distributorId: string, signal?: AbortSignal): Promise<number> => dispatch(stopOpenStreams(distributorId, signal)),
  };
};

const VodPurchaseModal: React.ComponentType<ModalState> = connect(mapStateToProps, mapDispatchToProps)(VodPurchaseModalView);

export default VodPurchaseModal;
